import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  AutoComplete,
  Badge,
  Button,
  Card,
  Descriptions,
  Flex,
  Form,
  Input,
  InputNumber,
  Popconfirm,
  Select,
  Switch,
  Table,
  Typography,
  message,
} from "antd";
import { CheckOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { getErrorMessage } from "@/utilities";
import { storePopulationsApi, StoreSeller } from "@/services/store-populations.api";
import {
  AGENT_PROVIDERS,
  AgentConfig,
  AgentRunsSummary,
  AgentStatus,
  agentApi,
} from "./api";
import {
  FormularioDelAsistente,
  MODELOS_SUGERIDOS,
  cambiosParaGuardar,
  costoEstimado,
  dolares,
  faltaParaEncender,
  formularioDesde,
  nombreDelMes,
} from "./agent-config";

const { Text } = Typography;

const PROVEEDORES: Record<string, string> = {
  anthropic: "Anthropic",
  openai: "OpenAI",
  google: "Google",
};

const fecha = (iso: string | null | undefined) =>
  iso ? dayjs(iso).format("DD/MM/YYYY HH:mm") : "—";

interface Props {
  storeCode: string;
  editable: boolean;
}

/**
 * Configuración del asistente (tarea 5.1): activo, nombre, proveedor, modelo, API key de sólo
 * escritura, seller preferido y límites; arriba, el estado del pod, el tope del día, el costo del mes
 * y el fusible. Guarda sólo lo que cambió.
 */
export const ConfiguracionTab: React.FC<Props> = ({ storeCode, editable }) => {
  const [form] = Form.useForm<FormularioDelAsistente>();
  const [config, setConfig] = useState<AgentConfig | null>(null);
  const [estado, setEstado] = useState<AgentStatus | null>(null);
  const [resumen, setResumen] = useState<AgentRunsSummary | null>(null);
  const [sellers, setSellers] = useState<StoreSeller[]>([]);
  const [saving, setSaving] = useState(false);
  const [levantando, setLevantando] = useState(false);
  const proveedor = Form.useWatch("provider", form);

  const inicial = useMemo(() => formularioDesde(config), [config]);

  useEffect(() => {
    agentApi
      .config(storeCode)
      .then((c) => {
        setConfig(c);
        form.setFieldsValue(formularioDesde(c));
      })
      .catch((e) => message.error(getErrorMessage(e)));
    // El estado y el costo son accesorios: si fallan, la configuración se edita igual.
    agentApi.estado(storeCode).then(setEstado).catch(() => setEstado(null));
    agentApi.resumen(storeCode).then(setResumen).catch(() => setResumen(null));
    storePopulationsApi.listSellers(storeCode).then(setSellers).catch(() => setSellers([]));
  }, [storeCode, form]);

  const guardar = async () => {
    const ahora = { ...inicial, ...form.getFieldsValue() };
    const cambios = cambiosParaGuardar(inicial, ahora);
    if (Object.keys(cambios).length === 0) {
      message.info("No hay cambios para guardar");
      return;
    }
    if (ahora.enabled) {
      const falta = faltaParaEncender(ahora, !!config?.hasApiKey);
      if (falta.length) {
        message.error(`Para encenderlo falta ${falta.join(", ")}`);
        return;
      }
    }
    setSaving(true);
    try {
      const c = await agentApi.guardar(storeCode, cambios);
      setConfig(c);
      form.setFieldsValue(formularioDesde(c));
      message.success("Configuración guardada. El asistente la toma en menos de un minuto.");
    } catch (e) {
      message.error(getErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const borrarKey = async () => {
    setSaving(true);
    try {
      // Sin key no puede quedar encendido: se apaga en el mismo guardado.
      const c = await agentApi.guardar(storeCode, { apiKey: null, enabled: false });
      setConfig(c);
      form.setFieldsValue(formularioDesde(c));
      message.success("API key borrada y asistente apagado");
    } catch (e) {
      message.error(getErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const levantarFusible = async () => {
    setLevantando(true);
    try {
      const r = await agentApi.levantarFusible(storeCode);
      setEstado((e) => (e ? { ...e, fuse: r.fuse } : e));
      setConfig((c) => (c ? { ...c, fuse: r.fuse } : c));
      message.success("Fusible levantado");
    } catch (e) {
      message.error(getErrorMessage(e));
    } finally {
      setLevantando(false);
    }
  };

  const fusible = (estado?.fuse ?? config?.fuse ?? []).find((f) => f.disabled);
  const budget = estado?.budget;

  return (
    <Flex vertical gap={16}>
      <Card size="small" title="Estado">
        <Descriptions size="small" column={{ xs: 1, sm: 2, md: 3 }}>
          <Descriptions.Item label="Asistente">
            {estado ? (
              <Badge
                status={estado.online ? "success" : "default"}
                text={estado.online ? "En línea" : "Sin conexión"}
              />
            ) : (
              "—"
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Último latido">{fecha(estado?.lastSeenAt)}</Descriptions.Item>
          <Descriptions.Item label="Versión">{estado?.baseVersion ?? "—"}</Descriptions.Item>
          <Descriptions.Item label="Tokens de hoy">
            {budget
              ? `${budget.tokensToday.toLocaleString("es-AR")}${
                  budget.maxTokensPerDay ? ` de ${budget.maxTokensPerDay.toLocaleString("es-AR")}` : " · sin tope"
                }`
              : "—"}
          </Descriptions.Item>
          <Descriptions.Item label={`Turnos de ${resumen ? nombreDelMes(resumen.month) : "este mes"}`}>
            {resumen ? resumen.totals.runs.toLocaleString("es-AR") : "—"}
          </Descriptions.Item>
        </Descriptions>

        {budget?.exhausted && (
          <Alert
            type="warning"
            showIcon
            style={{ marginTop: 12 }}
            message="Llegó al tope de tokens de hoy"
            description={`No responde solo hasta ${fecha(budget.resetsAt)}. Las menciones en notas siguen teniendo respuesta.`}
          />
        )}

        {fusible && (
          <Alert
            type="error"
            showIcon
            style={{ marginTop: 12 }}
            message={`El fusible está saltado hasta ${fecha(fusible.disabledUntil)}`}
            description={fusible.reason ?? "Hizo demasiados pedidos o tuvo demasiados errores en un minuto."}
            action={
              editable && (
                <Popconfirm
                  title="¿Levantar el fusible antes de tiempo?"
                  okText="Levantar"
                  cancelText="Cancelar"
                  onConfirm={levantarFusible}
                >
                  <Button size="small" loading={levantando}>
                    Levantar
                  </Button>
                </Popconfirm>
              )
            }
          />
        )}

        {resumen && resumen.byModel.length > 0 && (
          <>
            <Table
              style={{ marginTop: 12 }}
              size="small"
              pagination={false}
              rowKey={(r) => `${r.provider}/${r.model}`}
              dataSource={resumen.byModel}
              columns={[
                { title: "Modelo", render: (_, r) => r.model ?? "—" },
                { title: "Turnos", align: "right", render: (_, r) => r.runs.toLocaleString("es-AR") },
                {
                  title: "Tokens (entrada / salida)",
                  align: "right",
                  render: (_, r) =>
                    `${r.tokensIn.toLocaleString("es-AR")} / ${r.tokensOut.toLocaleString("es-AR")}`,
                },
                {
                  title: "Aprox. por turno",
                  align: "right",
                  render: (_, r) => {
                    const c = costoEstimado(r.model, r.tokensIn, r.tokensOut);
                    return c === null || r.runs === 0 ? "—" : dolares(c / r.runs);
                  },
                },
                {
                  title: "Aprox. del mes",
                  align: "right",
                  render: (_, r) => {
                    const c = costoEstimado(r.model, r.tokensIn, r.tokensOut);
                    return c === null ? "—" : dolares(c);
                  },
                },
              ]}
            />
            <Text type="secondary" style={{ display: "block", marginTop: 8, fontSize: 12 }}>
              Estimado con los precios de lista del proveedor, sin descuentos por caché. Es una referencia: el
              costo real está en la consola del proveedor.
            </Text>
          </>
        )}
      </Card>

      <Card size="small" title="Configuración">
        <Form form={form} layout="vertical" disabled={!editable} initialValues={inicial} style={{ maxWidth: 640 }}>
          <Form.Item name="enabled" label="Activo" valuePropName="checked" extra="Apagado, el asistente no lee ni responde en ningún canal.">
            <Switch />
          </Form.Item>

          <Form.Item name="displayName" label="Nombre" extra="Con este nombre firma y se lo menciona en las notas.">
            <Input maxLength={80} placeholder="Asistente de la tienda" />
          </Form.Item>

          <Flex gap={16} wrap="wrap">
            <Form.Item name="provider" label="Proveedor" style={{ minWidth: 180 }}>
              <Select options={AGENT_PROVIDERS.map((p) => ({ value: p, label: PROVEEDORES[p] ?? p }))} />
            </Form.Item>
            <Form.Item
              name="model"
              label="Modelo"
              style={{ flex: 1, minWidth: 260 }}
              rules={[{ pattern: /^[A-Za-z0-9._:/@-]{1,100}$/, message: "Sin espacios ni caracteres raros" }]}
            >
              <AutoComplete
                options={MODELOS_SUGERIDOS[proveedor ?? ""] ?? []}
                placeholder="claude-sonnet-5-5"
                filterOption={(input, o) => String(o?.value ?? "").includes(input.trim())}
              />
            </Form.Item>
          </Flex>

          <Form.Item
            label="API key"
            extra={
              config?.hasApiKey
                ? `Guardada: ${config.apiKeyHint ?? "••••"}. Escribí una nueva sólo para reemplazarla.`
                : "Se guarda cifrada y no se vuelve a mostrar."
            }
          >
            <Flex gap={8}>
              <Form.Item name="apiKey" noStyle>
                <Input.Password
                  autoComplete="new-password"
                  placeholder={config?.hasApiKey ? config.apiKeyHint ?? "••••" : "sk-…"}
                  visibilityToggle={false}
                />
              </Form.Item>
              {config?.hasApiKey && editable && (
                <Popconfirm
                  title="¿Borrar la API key?"
                  description="El asistente se apaga hasta que cargues otra."
                  okText="Borrar"
                  okButtonProps={{ danger: true }}
                  cancelText="Cancelar"
                  onConfirm={borrarKey}
                >
                  <Button danger>Borrar</Button>
                </Popconfirm>
              )}
            </Flex>
          </Form.Item>

          <Form.Item
            name="preferredSeller"
            label="Seller preferido"
            extra="Si un producto lo venden varios sellers, el presupuesto sale con éste."
          >
            <Select
              allowClear
              placeholder="El que resuelva la tienda"
              options={sellers.map((s) => ({ value: s.sellerCode, label: s.name }))}
            />
          </Form.Item>

          <Text strong>Límites</Text>
          <Text type="secondary" style={{ display: "block", marginBottom: 8 }}>
            Vacío es el valor por defecto.
          </Text>
          <Flex gap={16} wrap="wrap">
            <Form.Item name="maxTokensPerDay" label="Tokens por día" style={{ width: 200 }}>
              <InputNumber min={1000} max={100_000_000} step={100_000} style={{ width: "100%" }} placeholder="Sin tope" />
            </Form.Item>
            <Form.Item name="maxToolCallsPerTurn" label="Herramientas por turno" style={{ width: 200 }}>
              <InputNumber min={1} max={50} style={{ width: "100%" }} placeholder="12" />
            </Form.Item>
            <Form.Item name="maxTurnsPerConversation" label="Turnos por conversación" style={{ width: 200 }}>
              <InputNumber min={1} max={500} style={{ width: "100%" }} placeholder="Sin tope" />
            </Form.Item>
            <Form.Item name="ratePerMin" label="Pedidos por minuto" style={{ width: 200 }}>
              <InputNumber min={10} max={500} style={{ width: "100%" }} placeholder={String(config?.defaults.ratePerMin ?? 120)} />
            </Form.Item>
          </Flex>

          {config?.updatedAt && (
            <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
              Última modificación: {fecha(config.updatedAt)}
            </Text>
          )}

          {editable && (
            <Button type="primary" icon={<CheckOutlined />} iconPosition="end" loading={saving} onClick={guardar}>
              Guardar
            </Button>
          )}
        </Form>
      </Card>
    </Flex>
  );
};
